import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    let fallbackTimeoutId;
    let subscription;
    let hasHandledSuccess = false;

    const notifyVerificationSuccess = () => {
      try {
        localStorage.setItem(
          'email_verified',
          JSON.stringify({
            verified: true,
            timestamp: Date.now(),
          })
        );

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'EMAIL_VERIFIED' }, window.location.origin);
        }
      } catch (error) {
        console.error('Erreur de notification :', error);
      }
    };

    const handleSuccess = () => {
      if (!isMounted || hasHandledSuccess) return;

      hasHandledSuccess = true;
      setStatus('success');
      notifyVerificationSuccess();

      timeoutId = setTimeout(() => {
        navigate('/mon-compte', { replace: true });
      }, 1500);
    };

    const handleCallback = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error('Erreur callback auth :', error);
          if (isMounted) setStatus('error');
          return;
        }

        if (session && session.user) {
          handleSuccess();
          return;
        }

        const authListener = supabase.auth.onAuthStateChange((event, currentSession) => {
          if (!isMounted) return;

          if (
            (event === 'SIGNED_IN' ||
              event === 'TOKEN_REFRESHED' ||
              event === 'INITIAL_SESSION') &&
            currentSession &&
            currentSession.user
          ) {
            if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
            handleSuccess();
          }
        });

        subscription = authListener && authListener.data && authListener.data.subscription;

        fallbackTimeoutId = setTimeout(async () => {
          if (!isMounted || hasHandledSuccess) return;

          const {
            data: { session: retrySession },
          } = await supabase.auth.getSession();

          if (retrySession && retrySession.user) {
            handleSuccess();
          } else {
            setStatus('error');
          }
        }, 2500);
      } catch (err) {
        console.error('Erreur callback :', err);
        if (isMounted) setStatus('error');
      }
    };

    handleCallback();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
      if (subscription) subscription.unsubscribe();
    };
  }, [navigate]);

  return (
    <main className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16">
      <div className="max-w-xl w-full px-6">
        <div className="bg-white border border-border-subtle p-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-latte-gold/10 flex items-center justify-center">
              {status === 'success' ? (
                <CheckCircle2 className="w-8 h-8 text-latte-gold" />
              ) : status === 'error' ? (
                <AlertCircle className="w-8 h-8 text-red-500" />
              ) : (
                <Coffee className="w-8 h-8 text-latte-gold" />
              )}
            </div>
          </div>

          {status === 'loading' && (
            <>
              <h1 className="font-serif text-2xl text-espresso mb-2">
                Validation en cours
              </h1>
              <p className="text-soft-charcoal-secondary">
                Nous confirmons votre adresse email...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <h1 className="font-serif text-2xl text-espresso mb-2">
                Votre compte a bien été vérifié
              </h1>
              <p className="text-soft-charcoal-secondary">
                Votre adresse email a été confirmée avec succès.
              </p>
              <p className="text-soft-charcoal-secondary mt-4">
                Redirection vers votre compte...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="font-serif text-2xl text-espresso mb-2">
                Impossible de finaliser la confirmation
              </h1>
              <p className="text-soft-charcoal-secondary">
                Essayez de vous connecter ou redemandez un email de confirmation.
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  );
}